<template>
  <div class="home-page" style="display: flex; flex-direction: column">
    <!-- 应用广场：首屏英雄区已独立为「首页」，此处直接展示各内容板块 -->

    <!-- 提示词 · 技巧分享 -->
    <section
      v-if="promptList.length > 0 && showSection('prompts')"
      :style="sectionStyle('prompts')"
      class="py-12 md:py-16 bg-white border-b border-neutral-100"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="flex flex-col md:flex-row justify-between items-start md:items-center mb-8"
        >
          <div>
            <h2 class="text-2xl md:text-3xl font-bold mb-2 flex items-center">
              <span class="text-primary mr-2">✨</span>{{ sectionTitle('prompts', '提示词 · 技巧分享') }}
            </h2>
            <p class="text-neutral-500">{{ sectionSubtitle('prompts', '热门优质提示词与技术分享，即取即用') }}</p>
          </div>
          <router-link
            to="/prompts"
            class="text-primary text-sm font-medium hover:underline whitespace-nowrap"
          >
            查看全部 ›
          </router-link>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <div
            v-for="item in promptList"
            :key="item.id"
            class="bg-neutral-50 rounded-xl p-5 border border-neutral-200 hover:shadow-md hover:border-primary/40 transition-all cursor-pointer flex flex-col"
            @click="openPrompt(item)"
          >
            <div class="flex items-center justify-between mb-3">
              <span
                class="text-xs px-2 py-1 rounded-full"
                :class="item.type === 'skill'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-100 text-blue-700'"
              >
                {{ getPromptTypeText(item.type) }}
              </span>
              <span class="flex items-center gap-2">
                <span class="text-xs text-neutral-400">{{ item.category }}</span>
                <button
                  @click.stop="toggleMyApp('prompt', item.id)"
                  class="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                  :class="
                    inMyApps('prompt', item.id)
                      ? 'text-primary bg-primary/10'
                      : 'text-neutral-400 hover:text-primary bg-neutral-100'
                  "
                  :title="
                    inMyApps('prompt', item.id)
                      ? '已加入我的应用，点击移出'
                      : '加入我的应用'
                  "
                >
                  <i
                    class="text-xs"
                    :class="
                      inMyApps('prompt', item.id) ? 'fa fa-check' : 'fa fa-plus'
                    "
                  ></i>
                </button>
              </span>
            </div>
            <h3 class="font-semibold text-neutral-900 line-clamp-2 mb-3">
              {{ item.title }}
            </h3>
            <div class="flex-1"></div>
            <div class="flex items-center justify-between text-xs text-neutral-400 pt-3 border-t border-neutral-200">
              <span class="truncate mr-2">{{ item.userName || "匿名用户" }}</span>
              <span class="flex items-center gap-3 whitespace-nowrap">
                <span class="text-red-400">
                  <i class="fa fa-thumbs-o-up mr-1"></i>{{ item.likeCount || 0 }}
                </span>
                <span>
                  <i class="fa fa-eye mr-1"></i>{{ item.viewCount || 0 }}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 提示词详情弹窗 -->
    <el-dialog
      v-model="promptDialogVisible"
      :title="currentPrompt ? currentPrompt.title : ''"
      width="640px"
      top="8vh"
    >
      <div v-if="currentPrompt">
        <div class="flex items-center gap-3 mb-4 text-sm text-neutral-500">
          <span
            class="px-2 py-0.5 rounded-full text-xs"
            :class="currentPrompt.type === 'skill'
              ? 'bg-green-100 text-green-700'
              : 'bg-blue-100 text-blue-700'"
          >
            {{ getPromptTypeText(currentPrompt.type) }}
          </span>
          <span>{{ currentPrompt.category }}</span>
          <span>{{ currentPrompt.userName || "匿名用户" }}</span>
          <span class="flex items-center gap-3 ml-auto">
            <span class="text-red-400">
              <i class="fa fa-thumbs-o-up mr-1"></i>{{ currentPrompt.likeCount || 0 }}
            </span>
            <span>
              <i class="fa fa-eye mr-1"></i>{{ currentPrompt.viewCount || 0 }}
            </span>
          </span>
        </div>
        <pre class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-sm text-neutral-800 whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto">{{ currentPrompt.content }}</pre>
      </div>
      <template #footer>
        <button
          class="px-5 py-2 rounded-md border border-primary text-primary text-sm hover:bg-primary/5 transition-colors mr-3"
          @click="promptLikeAction"
        >
          <i class="fa" :class="currentPrompt && currentPrompt.isZan ? 'fa-thumbs-up' : 'fa-thumbs-o-up'"></i>
          {{ currentPrompt && currentPrompt.isZan ? "已点赞" : "点赞" }}
        </button>
        <button
          class="px-5 py-2 rounded-md border text-sm transition-colors mr-3"
          :class="
            currentPrompt && currentPrompt.isFav
              ? 'border-amber-400 text-amber-500 hover:bg-amber-50'
              : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'
          "
          @click="promptFavAction"
        >
          <i class="fa" :class="currentPrompt && currentPrompt.isFav ? 'fa-heart' : 'fa-heart-o'"></i>
          {{ currentPrompt && currentPrompt.isFav ? "已收藏" : "收藏" }}
        </button>
        <button
          class="px-5 py-2 rounded-md bg-primary text-white text-sm hover:bg-primary/90 transition-colors"
          @click="copyPrompt"
        >
          <i class="fa" :class="promptCopied ? 'fa-check' : 'fa-copy'"></i>
          {{ promptCopied ? "已复制" : "复制内容" }}
        </button>
      </template>
    </el-dialog>

    <!-- AI 智能体 -->
    <section
      v-if="agentList.length > 0 && showSection('agents')"
      :style="sectionStyle('agents')"
      class="py-16 bg-neutral-50 border-b border-neutral-100"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="flex flex-col md:flex-row justify-between items-start md:items-center mb-8"
        >
          <div>
            <h2 class="text-2xl md:text-3xl font-bold mb-2 flex items-center">
              <span class="text-primary mr-2">🤖</span>{{ sectionTitle('agents', 'AI 智能体') }}
            </h2>
            <p class="text-neutral-500">
              {{ sectionSubtitle('agents', '点开就能聊，不用再跳转外部平台') }}
            </p>
          </div>
          <router-link
            to="/agents"
            class="mt-4 md:mt-0 text-primary hover:underline text-sm font-medium"
          >
            查看全部 <i class="fa fa-arrow-right ml-1 text-xs"></i>
          </router-link>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div
            v-for="a in agentList"
            :key="a.id"
            class="relative bg-white rounded-2xl border border-neutral-200 p-5 hover:shadow-lg hover:border-primary transition-all duration-300 cursor-pointer flex flex-col group"
            @click="goAgentChat(a)"
          >
            <button
              @click.stop="toggleMyApp('agent', a.id)"
              class="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm transition-colors"
              :class="
                inMyApps('agent', a.id)
                  ? 'text-primary bg-primary/10'
                  : 'text-neutral-400 hover:text-primary'
              "
              :title="
                inMyApps('agent', a.id)
                  ? '已加入我的应用，点击移出'
                  : '加入我的应用'
              "
            >
              <i
                class="text-xs"
                :class="inMyApps('agent', a.id) ? 'fa fa-check' : 'fa fa-plus'"
              ></i>
            </button>
            <div class="flex items-start mb-3">
              <div
                class="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center text-white text-xl font-bold flex-shrink-0 overflow-hidden"
              >
                <img
                  v-if="a.avatar"
                  :src="a.avatar"
                  class="w-full h-full object-cover"
                />
                <span v-else>{{ agentFirstChar(a.name) }}</span>
              </div>
              <div class="ml-3 min-w-0 flex-1">
                <h3
                  class="font-bold text-base mb-1 group-hover:text-primary transition-colors line-clamp-1"
                >
                  {{ a.name }}
                </h3>
                <p class="text-xs text-neutral-400">{{ a.useCount || 0 }} 次对话</p>
              </div>
            </div>
            <p class="text-sm text-neutral-500 line-clamp-3 flex-1 mb-3">
              {{ a.intro || "暂无介绍" }}
            </p>
            <div v-if="a.tagList && a.tagList.length" class="flex flex-wrap gap-1 mb-4">
              <span
                v-for="(t, i) in a.tagList"
                :key="i"
                class="px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded text-xs"
                >{{ t }}</span
              >
            </div>
            <button
              class="w-full py-2 rounded-xl bg-primary/10 text-primary font-medium text-sm group-hover:bg-primary group-hover:text-white transition-colors"
            >
              开始对话 <i class="fa fa-arrow-right ml-1 text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- 工具展示区域 -->
    <section
      v-if="showSection('tools')"
      :style="sectionStyle('tools')"
      id="explore"
      class="py-16 bg-white"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="flex flex-col md:flex-row justify-between items-start md:items-center mb-10"
        >
          <div>
            <h2 class="text-2xl md:text-3xl font-bold mb-2">{{ sectionTitle('tools', '热门AI工具') }}</h2>
            <p class="text-neutral-500">{{ sectionSubtitle('tools', '由社区创建和推荐的最受欢迎的AI小工具') }}</p>
          </div>
          <div class="mt-4 md:mt-0 flex space-x-2">
            <button
              @click="getToolsList('lastest')"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium apple-transition',
                currentFilter === 'lastest'
                  ? 'bg-primary text-white'
                  : 'bg-white border border-neutral-300 hover:border-primary hover:text-primary',
              ]"
            >
              最新
            </button>
            <button
              @click="getToolsList('popular')"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium apple-transition',
                currentFilter === 'popular'
                  ? 'bg-primary text-white'
                  : 'bg-white border border-neutral-300 hover:border-primary hover:text-primary',
              ]"
            >
              最受欢迎
            </button>
            <button
              @click="getToolsList('rating')"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium apple-transition',
                currentFilter === 'rating'
                  ? 'bg-primary text-white'
                  : 'bg-white border border-neutral-300 hover:border-primary hover:text-primary',
              ]"
            >
              评分最高
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="tool in toolList"
            :key="tool.id"
            class="bg-white rounded-xl overflow-hidden border border-neutral-200 hover-lift tool-card cursor-pointer"
            @click="openToolDetail(tool)"
          >
            <div class="relative h-48 overflow-hidden group">
              <img
                :src="tool.cover"
                :alt="tool.title"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div class="absolute top-4 left-4">
                <span
                  class="px-3 py-1 bg-primary text-white text-xs rounded-full"
                  >{{ getCategoryName(tool.categoryid) }}</span
                >
                <span
                  v-if="getSubName(tool.subid).length > 0"
                  class="px-3 py-1 ml-2 bg-green-700 text-white text-xs rounded-full"
                  >{{ getSubName(tool.subid) }}</span
                >
              </div>
              <button
                @click.stop="favAction(tool)"
                class="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white apple-transition favorite-btn"
                :title="
                  tool.isFav ? '已加入我的应用，点击移出' : '加入我的应用'
                "
              >
                <i
                  v-if="tool.isFav"
                  class="fa fa-check text-primary apple-transition"
                ></i>
                <i
                  v-else
                  class="fa fa-plus text-neutral-600 apple-transition"
                ></i>
              </button>
            </div>
            <div class="p-5">
              <h3 class="text-lg font-bold mb-2">{{ tool.title }}</h3>
              <p class="text-neutral-500 text-sm mb-4 line-clamp-2 h-10">
                {{ tool.description }}
              </p>
              <div class="flex justify-between items-center">
                <div class="flex items-center">
                  <img
                    :src="tool.avatar"
                    alt="头像"
                    class="w-6 h-6 rounded-full mr-2"
                  />
                  <span class="text-xs text-neutral-500">{{ tool.authorName || tool.uname }}</span>
                </div>
                <div class="flex items-center space-x-3">
                  <span class="text-xs text-neutral-500"
                    ><i class="fa fa-download mr-1"></i>
                    {{ tool.downloadCount }}</span
                  >
                  <span class="text-xs text-neutral-500"
                    ><i class="fa fa-star text-yellow-400 mr-1"></i>
                    {{ tool.rate }}</span
                  >
                </div>
              </div>

              <div class="mt-4 flex space-x-3">
                <!-- 平台未开放下载时不展示"暂未开放下载"，改为引导查看详情 -->
                <button
                  @click.stop="
                    downloadEnabled ? downloadAction(tool) : openToolDetail(tool)
                  "
                  class="flex-1 px-3 py-2 text-white text-sm rounded-md apple-transition download-btn bg-primary hover:bg-primary/90 cursor-pointer"
                >
                  <i
                    :class="
                      'fa mr-1 ' + (downloadEnabled ? 'fa-download' : 'fa-eye')
                    "
                  ></i>
                  {{ downloadEnabled ? "下载" : "查看详情" }}
                  <span v-if="downloadEnabled && !tool.hasDownLoad && toolIsFree(tool)">(免费)</span>
                  <span v-else-if="downloadEnabled && !tool.hasDownLoad">(-{{ downloadCost }}创值)</span>
                </button>
                <button
                  @click.stop="zanAction(tool)"
                  class="w-9 h-9 border border-neutral-300 rounded-md flex items-center justify-center hover:border-primary hover:text-primary apple-transition like-btn"
                >
                  <i v-if="!tool.isZan" class="fa fa-thumbs-o-up"></i>
                  <i v-else class="fa fa-thumbs-up text-blue-500"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center mt-10">
          <router-link
            to="/explore"
            class="inline-block px-6 py-3 rounded-md bg-white border border-neutral-300 text-neutral-700 font-medium hover:border-primary hover:text-primary apple-transition"
          >
            加载更多 <i class="fa fa-chevron-down ml-1"></i>
          </router-link>
        </div>
      </div>
    </section>
 <!-- 最新需求 -->
    <section
      v-if="showSection('requests')"
      :style="sectionStyle('requests')"
      id="requests"
      class="py-16 bg-neutral-100"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-10">
          <div>
            <h2 class="text-2xl md:text-3xl font-bold mb-2">{{ sectionTitle('requests', '最新需求') }}</h2>
            <p class="text-neutral-500">{{ sectionSubtitle('requests', '来自社区的工具需求，等待您的解决方案') }}</p>
          </div>
          <router-link
            to="/request"
            class="mt-4 md:mt-0 px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90 transition-colors"
          >
            <i class="fa fa-plus mr-1"></i> 提出需求
          </router-link>
        </div>

        <!-- 需求列表 -->
        <div v-if="requestList.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="request in requestList.slice(0, 6)"
            :key="request.id"
            class="bg-white rounded-xl p-5 border border-neutral-200 hover:shadow-md transition-shadow duration-200 cursor-pointer"
            @click="openRequestDetail(request)"
          >
            <div class="flex items-start justify-between mb-3">
              <h3 class="font-semibold text-neutral-900 line-clamp-1 flex-1 mr-2">{{ request.title }}</h3>
              <span class="flex items-center gap-2 flex-shrink-0">
                <span class="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full whitespace-nowrap">
                  {{ getCategoryName(request.categoryId) }}
                </span>
                <button
                  @click.stop="toggleMyApp('request', request.id)"
                  class="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                  :class="
                    inMyApps('request', request.id)
                      ? 'text-primary bg-primary/10'
                      : 'text-neutral-400 hover:text-primary bg-neutral-100'
                  "
                  :title="
                    inMyApps('request', request.id)
                      ? '已加入我的应用，点击移出'
                      : '加入我的应用'
                  "
                >
                  <i
                    class="text-xs"
                    :class="
                      inMyApps('request', request.id)
                        ? 'fa fa-check'
                        : 'fa fa-plus'
                    "
                  ></i>
                </button>
              </span>
            </div>
            <p class="text-sm text-neutral-500 line-clamp-2 mb-4 h-10 whitespace-pre-line">{{ request.content }}</p>
            <div class="flex items-center justify-between text-xs text-neutral-400">
              <div class="flex items-center space-x-2">
                <img
                  :src="request.avatar || 'https://picsum.photos/id/64/100/100'"
                  alt="头像"
                  class="w-6 h-6 rounded-full"
                />
                <span>{{ request.uname || '匿名用户' }}</span>
              </div>
              <span class="flex items-center space-x-1">
                <i class="fa fa-comment-o"></i>
                <span>{{ request.replyCount || 0 }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else class="bg-white rounded-xl p-12 border border-neutral-200 text-center">
          <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-neutral-100 flex items-center justify-center">
            <i class="fa fa-lightbulb-o text-3xl text-neutral-400"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-700 mb-2">暂无需求</h3>
          <p class="text-sm text-neutral-500 mb-4">成为第一个提出需求的用户吧！</p>
          <router-link
            to="/request"
            class="inline-block px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90 transition-colors"
          >
            提出需求
          </router-link>
        </div>

        <!-- 查看更多 -->
        <div v-if="requestList.length > 0" class="text-center mt-8">
          <router-link
            to="/requests"
            class="inline-flex items-center px-6 py-3 rounded-md bg-white border border-neutral-300 text-neutral-700 font-medium hover:border-primary hover:text-primary transition-colors"
          >
            查看全部需求 <i class="fa fa-arrow-right ml-2"></i>
          </router-link>
        </div>
      </div>
    </section>

    <!-- 分类区域 -->
    <section
      id="categories"
      class="py-16 bg-white"
      v-if="categoryList && categoryList.length > 0 && showSection('categories')"
      :style="sectionStyle('categories')"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold mb-4">{{ sectionTitle('categories', '按分类浏览') }}</h2>
          <p class="text-neutral-500 max-w-2xl mx-auto">
            {{ sectionSubtitle('categories', '探索不同类别的AI工具，找到你需要的创意解决方案') }}
          </p>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
          <router-link
            v-for="category in categoryList.slice(0, 6)"
            :key="category.id"
            :to="{ path: '/categories', query: { category: category.id } }"
            class="category-card bg-white rounded-xl p-5 text-center border border-neutral-200 hover-lift"
          >
            <div
              class="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
              :style="{
                backgroundColor: category.color + '20',
              }"
            >
              <i
                :class="['fa', category.icon, 'text-xl']"
                :style="{ color: category.color }"
              ></i>
            </div>
            <h3 class="font-medium text-sm">{{ category.title }}</h3>
            <p class="text-xs text-neutral-500 mt-1">
              {{ category.toolCount }} 个工具
            </p>
          </router-link>
        </div>

        <div class="text-center mt-8">
          <router-link
            to="/categories"
            class="text-primary font-medium hover:underline"
          >
            查看所有分类 <i class="fa fa-arrow-right ml-1"></i>
          </router-link>
        </div>
      </div>
    </section>

    <!-- 学科区域 -->
    <section
      id="subjects"
      class="py-16 bg-white"
      v-if="subList && subList.length > 0 && showSection('subjects')"
      :style="sectionStyle('subjects')"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold mb-4">按学科浏览</h2>
          <p class="text-neutral-500 max-w-2xl mx-auto">
            探索不同学科领域的AI工具，辅助您的学习与研究
          </p>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <router-link
            v-for="subject in subList.slice(0, 12)"
            :key="subject.id"
            :to="{ path: '/subjects', query: { subject: subject.id } }"
            class="group bg-neutral-50 rounded-lg p-4 text-center border border-neutral-200 hover:border-primary hover:shadow-sm transition-all duration-300"
          >
            <h3 class="font-medium text-sm group-hover:text-primary transition-colors">{{ subject.title }}</h3>
            <!-- <p class="text-xs text-neutral-500 mt-1">
              {{ subject.toolCount || 0 }} 个工具
            </p> -->
          </router-link>
        </div>

        <div class="text-center mt-8">
          <router-link
            to="/subjects"
            class="text-primary font-medium hover:underline"
          >
            查看所有学科 <i class="fa fa-arrow-right ml-1"></i>
          </router-link>
        </div>
      </div>
    </section>

    <!-- 软件分享 -->
    <section
      v-if="softwareList.length > 0 && showSection('software')"
      :style="sectionStyle('software')"
      class="py-16 bg-neutral-50 border-b border-neutral-100"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="flex flex-col md:flex-row justify-between items-start md:items-center mb-8"
        >
          <div>
            <h2 class="text-2xl md:text-3xl font-bold mb-2 flex items-center">
              <span class="text-primary mr-2">📦</span>{{ sectionTitle('software', '软件分享') }}
            </h2>
            <p class="text-neutral-500">
              {{ sectionSubtitle('software', '管理员精选的实用软件，仅作分享交流使用') }}
            </p>
          </div>
          <router-link
            to="/software"
            class="mt-4 md:mt-0 text-primary hover:underline text-sm font-medium"
          >
            查看全部 <i class="fa fa-arrow-right ml-1 text-xs"></i>
          </router-link>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div
            v-for="s in softwareList"
            :key="s.id"
            class="relative bg-white rounded-2xl border border-neutral-200 p-5 hover:shadow-lg hover:border-primary transition-all duration-300 cursor-pointer flex flex-col group"
            @click="goSoftware"
          >
            <button
              @click.stop="toggleMyApp('software', s.id)"
              class="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm transition-colors"
              :class="
                inMyApps('software', s.id)
                  ? 'text-primary bg-primary/10'
                  : 'text-neutral-400 hover:text-primary'
              "
              :title="
                inMyApps('software', s.id)
                  ? '已加入我的应用，点击移出'
                  : '加入我的应用'
              "
            >
              <i
                class="text-xs"
                :class="
                  inMyApps('software', s.id) ? 'fa fa-check' : 'fa fa-plus'
                "
              ></i>
            </button>
            <div class="flex items-start mb-3">
              <div
                class="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0"
              >
                <img
                  v-if="s.cover"
                  :src="s.cover"
                  class="w-full h-full object-cover"
                />
                <i v-else class="fa fa-cube text-neutral-400 text-xl"></i>
              </div>
              <div class="ml-3 min-w-0 flex-1">
                <h3
                  class="font-bold text-base mb-1 group-hover:text-primary transition-colors line-clamp-1"
                >
                  {{ s.title }}
                </h3>
                <p class="text-xs text-neutral-400">
                  {{ s.downloadCount || 0 }} 次下载
                </p>
              </div>
            </div>
            <p class="text-sm text-neutral-500 line-clamp-2 flex-1 mb-3">
              {{ s.intro || "暂无简介" }}
            </p>
            <span
              class="px-2 py-0.5 rounded text-xs self-start"
              :class="
                s.shareType === 'file'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-blue-50 text-blue-700'
              "
              >{{ s.shareType === "file" ? "站内附件" : s.panName || "网盘" }}</span
            >
          </div>
        </div>
      </div>
    </section>

    <!-- 排行榜区域 -->
    <section
      id="leaderboard"
      class="py-16 bg-white"
      v-if="showSection('leaderboard')"
      :style="sectionStyle('leaderboard')"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold mb-4">工具排行榜</h2>
          <p class="text-neutral-500 max-w-2xl mx-auto">
            最受欢迎和评分最高的AI工具
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- 热门下载排行榜 -->
          <div class="bg-neutral-100 rounded-xl p-6">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-xl font-bold">热门下载</h3>
              <router-link
                to="/rankings"
                class="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full hover:bg-primary/20 transition-colors"
                >查看全部</router-link
              >
            </div>

            <div class="space-y-4">
              <div
                v-for="(tool, index) in rankDownloadList"
                :key="tool.id"
                class="flex items-center p-3 bg-white rounded-lg border border-neutral-200 hover:shadow-md transition-shadow cursor-pointer"
                @click="openToolDetail(tool)"
              >
                <div
                  :class="[
                    'w-8 h-8 rounded-full flex items-center justify-center font-bold mr-4 text-white',
                    index === 0
                      ? 'bg-primary'
                      : index === 1
                      ? 'bg-neutral-400'
                      : 'bg-neutral-300',
                  ]"
                >
                  {{ index + 1 }}
                </div>
                <img
                  :src="tool.cover"
                  :alt="tool.title"
                  class="w-12 h-12 rounded-lg object-cover mr-4"
                />
                <div class="flex-1 min-w-0">
                  <h4 class="font-medium truncate">{{ tool.title }}</h4>
                  <p class="text-xs text-neutral-500">
                    {{ getCategoryName(tool.categoryid) }} ·
                    <span v-if="getSubName(tool.subid).length > 0">{{
                      getSubName(tool.subid)
                    }} · </span>
                    {{ formatNumber(tool.monthDownloadCount) }} 下载
                  </p>
                </div>
                <div class="flex items-center">
                  <i class="fa fa-download text-stone-400 mr-1"></i>
                  <span>{{ tool.monthDownloadCount }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 最高评分排行榜 -->
          <div class="bg-neutral-100 rounded-xl p-6">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-xl font-bold">最高评分</h3>
              <router-link
                to="/rankings"
                class="px-3 py-1 bg-secondary/10 text-secondary text-xs rounded-full hover:bg-secondary/20 transition-colors"
                >查看全部</router-link
              >
            </div>

            <div class="space-y-4">
              <div
                v-for="(tool, index) in topRatedTools"
                :key="tool.id"
                class="flex items-center p-3 bg-white rounded-lg border border-neutral-200 hover:shadow-md transition-shadow cursor-pointer"
                @click="openToolDetail(tool)"
              >
                <div
                  :class="[
                    'w-8 h-8 rounded-full flex items-center justify-center font-bold mr-4 text-white',
                    index === 0
                      ? 'bg-secondary'
                      : index === 1
                      ? 'bg-neutral-400'
                      : 'bg-neutral-300',
                  ]"
                >
                  {{ index + 1 }}
                </div>
                <img
                  :src="tool.cover"
                  :alt="tool.title"
                  class="w-12 h-12 rounded-lg object-cover mr-4"
                />
                <div class="flex-1 min-w-0">
                  <h4 class="font-medium truncate">{{ tool.title }}</h4>
                  <p class="text-xs text-neutral-500">
                    {{ getCategoryName(tool.categoryid) }} ·
                     <span v-if="getSubName(tool.subid).length > 0">{{
                      getSubName(tool.subid)
                    }} · </span>
                    {{ formatNumber(tool.downloadCount) }} 下载
                  </p>
                </div>
                <div class="flex items-center">
                  <i class="fa fa-star text-yellow-400 mr-1"></i>
                  <span>{{ tool.rate }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import index from "./index.js";
export default index;
</script>

<style scoped>
.banner-fade-enter-active,
.banner-fade-leave-active {
  transition: opacity 0.6s ease;
}
.banner-fade-enter,
.banner-fade-enter-from,
.banner-fade-leave-to {
  opacity: 0;
}

.ask-badge {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4facfe, #0071e3);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ask-go {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #0071e3;
  color: #fff;
  flex-shrink: 0;
  transition: opacity 0.2s;
}
.ask-go:hover {
  opacity: 0.85;
}
</style>
